import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { playersAPI, adminAPI } from '../lib/api';
import { getInitials, getSkillLevelColor, calculateWinRate } from '../lib/utils';
import { PowerLevelInline } from '../components/PowerLevelBadge';
import { StreakFire } from '../components/TrophyCase';
import { Search, Users, ChevronRight, UserPlus } from 'lucide-react';

const skillLevels = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'professional', label: 'Professional' },
];

export default function Players() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPlayer, setNewPlayer] = useState({ name: '', email: '', skill_level: 'beginner' });

  const isAdmin = ['platform_admin', 'club_admin'].includes(user?.role);

  const fetchPlayers = async () => {
    try {
      const response = await playersAPI.getAll();
      setPlayers(response.data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlayers(); }, []);

  const handleAddPlayer = async () => {
    if (!newPlayer.name || !newPlayer.email) {
      toast.error('Name and email are required');
      return;
    }
    setCreating(true);
    try {
      const response = await adminAPI.createPlayer({ ...newPlayer, role: 'player' });
      toast.success(`Player added! Default password: ${response.data.default_password}`);
      setAddDialogOpen(false);
      setNewPlayer({ name: '', email: '', skill_level: 'beginner' });
      fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    } finally {
      setCreating(false);
    }
  };

  const filteredPlayers = players.filter((player) =>
    player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    player.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-48" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="players-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t('players.title')}</h1>
            <p className="text-muted-foreground text-sm mt-1">{players.length} registered {t('players.title').toLowerCase()}</p>
          </div>
          <Button onClick={() => {
              if (!isAuthenticated) { navigate('/login'); return; }
              setAddDialogOpen(true);
            }} className="bg-secondary hover:bg-secondary/90" data-testid="add-player-btn">
              <UserPlus className="h-4 w-4 mr-2" />Add Player
            </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder={`${t('common.search')} ${t('players.title').toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {/* Players Grid */}
        {filteredPlayers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No players found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPlayers.map((player) => (
              <Link key={player.user_id} to={`/players/${player.user_id}`}>
                <Card className="hover:border-primary/50 hover:shadow-md transition-all h-full">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-11 w-11">
                          <AvatarImage src={player.picture} />
                          <AvatarFallback className="bg-primary/20 text-primary">{getInitials(player.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-heading font-bold">{player.name}</h3>
                            {player.stats?.current_streak >= 3 && <StreakFire currentStreak={player.stats.current_streak} />}
                          </div>
                          <Badge className={`${getSkillLevelColor(player.skill_level)} text-[10px] capitalize`}>{player.skill_level}</Badge>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground mt-1" />
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-center mt-3 pt-3 border-t border-border">
                      <div>
                        <p className="text-lg font-bold">{player.stats?.matches_played || 0}</p>
                        <p className="text-[10px] text-muted-foreground uppercase">{t('nav.matches')}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-secondary">
                          {calculateWinRate(player.stats?.matches_won, player.stats?.matches_played)}%
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase">{t('dashboard.winRate')}</p>
                      </div>
                      <div>
                        <PowerLevelInline elo={player.stats?.elo_rating || 1200} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Add Player Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add New Player</DialogTitle>
              <DialogDescription>Add a new player to the system. A default password will be created.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input placeholder="John Doe" value={newPlayer.name} onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })} data-testid="new-player-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" placeholder="john@example.com" value={newPlayer.email} onChange={(e) => setNewPlayer({ ...newPlayer, email: e.target.value })} data-testid="new-player-email" />
              </div>
              <div className="space-y-2">
                <Label>Skill Level</Label>
                <Select value={newPlayer.skill_level} onValueChange={(v) => setNewPlayer({ ...newPlayer, skill_level: v })}>
                  <SelectTrigger data-testid="new-player-skill"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {skillLevels.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleAddPlayer} disabled={creating} className="bg-secondary hover:bg-secondary/90" data-testid="confirm-add-player">
                {creating ? 'Adding...' : 'Add Player'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
